import { normalizeMarker } from './domain.js';
export const STORYLINE_KINDS = ['read', 'video', 'title', 'sot', 'reporter', 'card', 'natsound', 'promo', 'note'];
// ── Markers as journalists type them: Greek capitals, often with Latin look-alikes (TITΛΟΣ, ON), a colon or not ──
const GREEK_TO_LATIN = { Α: 'A', Β: 'B', Ε: 'E', Ζ: 'Z', Η: 'H', Ι: 'I', Κ: 'K', Μ: 'M', Ν: 'N', Ο: 'O', Ρ: 'P', Τ: 'T', Χ: 'X', Υ: 'Y' };
/** A word as a pattern that accepts a Latin look-alike for each Greek capital that has one. */
const w = (word) => word.replace(/[ΑΒΕΖΗΙΚΜΝΟΡΤΧΥ]/g, (c) => `[${c}${GREEK_TO_LATIN[c]}]`);
const re = (src) => new RegExp(src, 'iu');
const RE = {
    on: re(`^${w('ΟΝ')}\\s*:?$`),
    vo: re(`^(?:${w('ΣΠΗΚΑΖ')}|${w('ΣΠΙΚΑΖ')}|V\\.?${w('Ο')}\\.?)\\s*:?$`),
    videoSound: re(`^${w('ΒΙΝΤΕΟ')}(?:\\s+${w('ΜΕ')}\\s+${w('ΗΧΟ')})?\\s*:?$`),
    videoMute: re(`^(?:${w('ΒΙΝΤΕΟ')}\\s+${w('ΧΩΡΙΣ')}\\s+${w('ΗΧΟ')}|(?:${w('ΒΙΝΤΕΟ')}|VIDEO)\\s+V\\.?${w('Ο')}\\.?|\\(?${w('ΦΙΛΕΡ')}\\)?)\\s*:?$`),
    title: re(`^(?:${w('ΤΙΤΛΟΣ')}|${w('ΦΑΣΑ')}|${w('ΦΑΣΕΣ')})\\s*:?\\s*(\\d+)?\\s*[:.\\-–—]?\\s*(.*?)\\s*$`),
    /** ΔΗΛΩΣΗ ΜΕΝΔΡΗ: Και πρέπει… — the speaker and the words on one line. */
    sotInline: re(`^${w('ΔΗΛΩΣΗ')}\\s*[\\-–—]?\\s*([^:]{1,60}?)\\s*:\\s*(\\S.*)$`),
    /** ΔΗΛΩΣΗ, ΔΗΛΩΣΗ:, ΔΗΛΩΣΗ (ΜΕ ΥΠΟΤΙΤΛΟΥΣ), ΔΗΛΩΣΗ - ΠΑΡΑΣΚΕΥΗ ΛΟΥΛΗ: */
    sot: re(`^${w('ΔΗΛΩΣΗ')}\\s*(?:\\([^)]*\\))?\\s*(?:[:\\-–—]\\s*(.*?))?\\s*:?$`),
    reporter: re(`^${w('ΡΕΠΟΡΤΑΖ')}\\s*:?\\s*(.*?)\\s*$`),
    camera: re(`^${w('ΚΑΜΕΡΑ')}\\s*:?\\s*(.*?)\\s*$`),
    card: re(`^${w('ΚΑΡΤΑ')}\\s*(\\d+)?\\s*:?$`),
    natsound: re(`^${w('ΦΥΣΙΚΟΣ')}\\s+${w('ΗΧΟΣ')}\\s*:?$`),
    promo: re(`^(?:ALPHANEWS\\.?LIVE|${w('ΔΕΙΤΕ')}\\s+${w('ΚΥΡΙΕΣ')}\\s+${w('ΚΑΙ')}\\s+${w('ΚΥΡΙΟΙ')}\\s+${w('ΣΕ')}\\s+${w('ΛΙΓΟ')})\\s*:?$`),
};
const SEPARATOR = /^[\s\-—–_=.·*]{3,}$/;
const hasLower = (t) => /\p{Ll}/u.test(t);
const hasLetters = (t) => /\p{L}/u.test(t);
/** A speaker's or a reporter's name: capitals, or a few capitalised words without sentence punctuation. */
const nameLike = (t) => hasLetters(t) && t.length <= 50 && (!hasLower(t) || (t.split(/\s+/).length <= 5 && /^\p{Lu}/u.test(t) && !/[.!;?»"”]$/.test(t)));
/** A role under a name: short, not a sentence. */
const roleLike = (t) => t.length <= 70 && !/[.!;?»"”]$/.test(t) && !/^[«"“]/.test(t);
/** A line that reads as a heading rather than prose: capitals only, short. */
const markerLike = (t) => t.length <= 50 && hasLetters(t) && !hasLower(t);
/** The boxes of a script, best effort on free text; exact on text this module rendered. */
export function parseStoryline(text) {
    const blocks = [];
    /** The block that collects the next lines, and what it expects. */
    let cur = null;
    let state = 'none';
    let lastReadMode = null;
    const push = (b) => {
        blocks.push(b);
        cur = b;
        state = 'none';
        if (b.kind === 'read')
            lastReadMode = b.mode;
    };
    const prose = (t) => {
        // free text with no heading collecting it: the presenter reads it, or the voice-over continues after pictures
        const prev = blocks[blocks.length - 1];
        const mode = prev && (prev.kind === 'video' || prev.kind === 'natsound') ? 'vo' : (lastReadMode ?? 'on');
        push({ kind: 'read', mode, text: t });
    };
    for (const raw of text.normalize('NFC').split(/\r?\n/)) {
        const t = raw.replace(/ /g, ' ').trim();
        if (!t) {
            // a blank line closes what a heading collects (a title's lines, a card, a speaker's name and role)
            const c0 = cur; // `cur` is also set inside push(), which the flow analysis does not see
            if (c0 && c0.kind !== 'read' && c0.kind !== 'sot')
                cur = null;
            if (c0?.kind === 'sot')
                state = 'text';
            continue;
        }
        if (SEPARATOR.test(t))
            continue;
        let m;
        if (RE.on.test(t)) {
            push({ kind: 'read', mode: 'on', text: '' });
            continue;
        }
        if (RE.vo.test(t)) {
            push({ kind: 'read', mode: 'vo', text: '' });
            continue;
        }
        if (RE.videoMute.test(t)) {
            push({ kind: 'video', sound: false, note: '' });
            continue;
        }
        if (RE.videoSound.test(t)) {
            push({ kind: 'video', sound: true, note: '' });
            continue;
        }
        if ((m = RE.title.exec(t))) {
            push({ kind: 'title', n: m[1] ? Number(m[1]) : null, lines: m[2] ? [m[2]] : [] });
            continue;
        }
        if ((m = RE.sotInline.exec(t)) && nameLike(m[1].trim())) {
            push({ kind: 'sot', name: m[1].trim(), role: '', text: m[2].trim() });
            state = 'text';
            continue;
        }
        if ((m = RE.sot.exec(t))) {
            const inline = (m[1] ?? '').trim();
            // ΔΗΛΩΣΗ - ΠΑΡΑΣΚΕΥΗ ΛΟΥΛΗ: names the speaker; ΔΗΛΩΣΗ: Το ανακάλυψα… starts the words
            const named = inline !== '' && nameLike(inline);
            push({ kind: 'sot', name: named ? inline : '', role: '', text: named ? '' : inline });
            state = named ? 'role' : inline ? 'text' : 'name';
            continue;
        }
        if ((m = RE.reporter.exec(t))) {
            const name = (m[1] ?? '').trim();
            push({ kind: 'reporter', name, email: '', camera: '' });
            state = name ? 'email' : 'name';
            continue;
        }
        if ((m = RE.camera.exec(t))) {
            const name = (m[1] ?? '').trim();
            const prev = blocks[blocks.length - 1];
            if (prev && prev.kind === 'reporter' && !prev.camera) {
                prev.camera = name;
                cur = prev;
            }
            else
                push({ kind: 'reporter', name: '', email: '', camera: name });
            state = name ? 'none' : 'camera';
            continue;
        }
        if ((m = RE.card.exec(t))) {
            push({ kind: 'card', n: m[1] ? Number(m[1]) : null, lines: [] });
            continue;
        }
        if (RE.natsound.test(t)) {
            push({ kind: 'natsound' });
            cur = null;
            continue;
        }
        if (RE.promo.test(t)) {
            push({ kind: 'promo', lines: [] });
            continue;
        }
        // a line the current heading collects
        if (cur) {
            const c = cur;
            if (c.kind === 'title' && c.lines.length < 2 && (!hasLower(t) || t.length <= 80)) {
                c.lines.push(t);
                continue;
            }
            if (c.kind === 'card' || c.kind === 'promo') {
                c.lines.push(t);
                continue;
            }
            if (c.kind === 'video') {
                c.note = c.note ? `${c.note}\n${t}` : t;
                continue;
            }
            if (c.kind === 'note' && markerLike(t)) {
                c.text = `${c.text}\n${t}`;
                continue;
            }
            if (c.kind === 'sot') {
                if (state === 'name' && nameLike(t)) {
                    c.name = t;
                    state = 'role';
                    continue;
                }
                if ((state === 'name' || state === 'role') && roleLike(t) && hasLower(t)) {
                    c.role = t;
                    state = 'text';
                    continue;
                }
                c.text = c.text ? `${c.text}\n${t}` : t;
                state = 'text';
                continue;
            }
            if (c.kind === 'reporter') {
                if (state === 'name' && nameLike(t)) {
                    c.name = t;
                    state = 'email';
                    continue;
                }
                if ((state === 'email' || state === 'name') && /@/.test(t)) {
                    c.email = t;
                    state = 'camera';
                    continue;
                }
                if (state === 'camera' && nameLike(t)) {
                    c.camera = t;
                    state = 'none';
                    cur = null;
                    continue;
                }
                // anything else ends the credits
                cur = null;
            }
            if (c.kind === 'read') {
                if (markerLike(t)) {
                    push({ kind: 'note', text: t });
                    continue;
                }
                c.text = c.text ? `${c.text}\n${t}` : t;
                continue;
            }
        }
        if (markerLike(t)) {
            push({ kind: 'note', text: t });
            continue;
        }
        prose(t);
    }
    return blocks;
}
const RULE = '--------------------------------------------------';
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const paras = (s) => s.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
/** The heading a block writes into the script, or null for text-only boxes. */
export function storylineHeading(b) {
    switch (b.kind) {
        case 'read':
            return b.mode === 'on' ? 'ON' : 'ΣΠΗΚΑΖ';
        case 'video':
            return b.sound ? 'ΒΙΝΤΕΟ ΜΕ ΗΧΟ' : 'ΦΙΛΕΡ';
        case 'title':
            return b.n !== null ? `ΤΙΤΛΟΣ ${b.n}` : 'ΤΙΤΛΟΣ';
        case 'sot':
            return 'ΔΗΛΩΣΗ';
        case 'reporter':
            return 'ΡΕΠΟΡΤΑΖ';
        case 'card':
            return b.n !== null ? `ΚΑΡΤΑ ${b.n}` : 'ΚΑΡΤΑ';
        case 'natsound':
            return 'ΦΥΣΙΚΟΣ ΗΧΟΣ';
        case 'promo':
            return 'ALPHANEWS.LIVE';
        case 'note':
            return null;
    }
}
/** The lines of one block as they sit in the script. */
function blockLines(b, last) {
    const h = storylineHeading(b);
    switch (b.kind) {
        case 'read':
            return [h, ...paras(b.text), ...(b.mode === 'on' && !last ? [RULE] : [])];
        case 'video':
            return [h, ...paras(b.note)];
        case 'title':
            return [h, ...b.lines.map((l) => l.trim()).filter(Boolean)];
        case 'sot':
            return [h, ...(b.name.trim() ? [b.name.trim()] : []), ...(b.role.trim() ? [b.role.trim()] : []), ...paras(b.text)];
        case 'reporter':
            return [h, ...(b.name.trim() ? [b.name.trim()] : []), ...(b.email.trim() ? [b.email.trim()] : []), ...(b.camera.trim() ? ['ΚΑΜΕΡΑ', b.camera.trim()] : [])];
        case 'card':
        case 'promo':
            return [h, ...b.lines.map((l) => l.trim()).filter(Boolean)];
        case 'natsound':
            return [h];
        case 'note':
            return paras(b.text);
    }
}
/** The script of the boxes: the text (one line per paragraph, a blank line between boxes) and the editor's HTML. */
export function renderStoryline(blocks) {
    const groups = blocks.map((b, i) => blockLines(b, i === blocks.length - 1)).filter((g) => g.length > 0);
    const text = groups.map((g) => g.join('\n')).join('\n\n');
    const html = groups.map((g) => g.map((l) => `<p>${esc(l)}</p>`).join('')).join('<p></p>') || '<p></p>';
    return { text, html };
}
/** The storyboard anchor of each box (`ΒΙΝΤΕΟ ΜΕ ΗΧΟ#1`, `ΤΙΤΛΟΣ 2#1`), as the slot scanner names the headings; null for boxes without a cue. */
const VO_ANCHOR = normalizeMarker('ΦΙΛΕΡ');
const SOUND_ANCHOR = normalizeMarker('ΒΙΝΤΕΟ ΜΕ ΗΧΟ');
/**
 * Does a rundown row's clip play without its sound (a ΒΙΝΤΕΟ VO box, written ΦΙΛΕΡ in the script), so that the playout
 * puts it on its VO player? The storyboard cue carrying the clip decides by the box it sits under; a clip without such
 * a cue (chosen on the row by hand, or a story without a storyline) follows the row's kind.
 */
export function clipIsVo(item, cues) {
    const cue = item.mediaAssetId ? cues.find((c) => c.mediaAssetId === item.mediaAssetId) : undefined;
    const box = cueBox(cue?.anchor ?? null);
    if (box)
        return box === 'vo';
    return item.kind === 'vo' || item.kind === 'filler';
}
/** The box a video cue sits under: ΒΙΝΤΕΟ VO (`ΦΙΛΕΡ`), ΒΙΝΤΕΟ ΜΕ ΗΧΟ, or neither (a cue added by hand). */
function cueBox(anchor) {
    const heading = anchor ? normalizeMarker(anchor.split('#')[0]) : null;
    return heading === VO_ANCHOR ? 'vo' : heading === SOUND_ANCHOR ? 'sound' : null;
}
/**
 * The story's other clips for a rundown row (ADR 086): the row plays its own clip (the story's first video), and the
 * playout gets every other video cue with a clip as well, in storyboard order, each for the player of its box: a
 * ΒΙΝΤΕΟ VO box's for the VO player, the others' (ΒΙΝΤΕΟ ΜΕ ΗΧΟ, a cue added by hand) for the bulletin's player.
 */
export function rowClips(item, cues) {
    let ownSeen = false;
    const out = [];
    for (const c of cues) {
        if (!c.mediaAssetId)
            continue;
        // the row's own clip: the first cue carrying it, as `clipIsVo` reads it
        if (!ownSeen && c.mediaAssetId === item.mediaAssetId) {
            ownSeen = true;
            continue;
        }
        out.push({ part: c.id, mediaAssetId: c.mediaAssetId, inMs: c.inMs, outMs: c.outMs, vo: cueBox(c.anchor) === 'vo' });
    }
    return out;
}
/** What a story puts on air, by its boxes: the rundown shows these after its title (ADR 087). */
export const CONTENT_KINDS = ['video-sound', 'video-vo', 'title', 'sot', 'reporter', 'card', 'natsound', 'promo'];
/** A story's boxes by what they put on air, counted, in the order of CONTENT_KINDS; the presenter's words and notes are not counted. */
export function storylineContents(blocks) {
    const n = new Map();
    for (const b of blocks) {
        const kind = b.kind === 'video' ? (b.sound ? 'video-sound' : 'video-vo') : b.kind === 'read' || b.kind === 'note' ? null : b.kind;
        if (kind)
            n.set(kind, (n.get(kind) ?? 0) + 1);
    }
    return CONTENT_KINDS.filter((k) => n.has(k)).map((kind) => ({ kind, n: n.get(kind) }));
}
/** Titles and credits can ride on the pictures of the box before them (given a time), instead of taking a step of their own. */
export const isOverlayKind = (k) => k === 'title' || k === 'reporter';
/** Boxes that take air time and can carry such a title: pictures or words. */
export const isCarrierKind = (k) => k === 'video' || k === 'read' || k === 'sot' || k === 'natsound';
/** The box a title at `i` would ride on: the nearest earlier carrier, with nothing but other titles in between; null when none. */
export function overlayCarrier(blocks, i) {
    const b = blocks[i];
    if (!b || !isOverlayKind(b.kind))
        return null;
    for (let k = i - 1; k >= 0; k--) {
        const kind = blocks[k].kind;
        if (isCarrierKind(kind))
            return k;
        if (!isOverlayKind(kind))
            return null;
    }
    return null;
}
/**
 * Boxes inside boxes ("anything into anything"). Any box but a note can sit inside the box before it: a title or a
 * video inside an ON, an ON over a video, a card inside a statement. Titles and credits keep their second on their CG
 * cue (`inMs`), as the video hub and the edit apply write it; every other kind keeps it in the story's `storylineAt`
 * map, keyed by `boxKeys`. A box sits inside the nearest earlier box that is neither a note nor inside something
 * itself (one level: a title after a video that is itself inside an ON sits in the ON, at its own second).
 */
export const canRide = (k) => k !== 'note';
/** Boxes other boxes can sit inside: everything that takes air time and is not itself a strap or a credit. */
export const canCarry = (k) => k !== 'note' && !isOverlayKind(k);
/** A key for every box but a note (`ON#1`, `ΒΙΝΤΕΟ ΜΕ ΗΧΟ#2`, `ΤΙΤΛΟΣ 1#1`): the cue anchor where the box has one. */
export function boxKeys(blocks) {
    const counts = new Map();
    return blocks.map((b) => {
        const h = storylineHeading(b);
        if (!h)
            return null;
        const label = normalizeMarker(h);
        const n = (counts.get(label) ?? 0) + 1;
        counts.set(label, n);
        return `${label}#${n}`;
    });
}
/**
 * Which box each box sits inside (box index → carrier index), given the second each box asks for (`atOf`, null =
 * a step of its own). A box with a second but no box before it to sit in is a step of its own.
 */
export function insideOf(blocks, atOf) {
    const out = new Map();
    blocks.forEach((b, i) => {
        if (!canRide(b.kind) || atOf(i) === null)
            return;
        const carrier = carrierBefore(blocks, i, out);
        if (carrier !== null)
            out.set(i, carrier);
    });
    return out;
}
/**
 * The box a box at `i` would sit inside: the nearest earlier carrier that is not itself inside another box (notes,
 * titles and credits in between are passed over, as titles always were).
 */
export function carrierBefore(blocks, i, inside) {
    for (let k = i - 1; k >= 0; k--) {
        const kind = blocks[k].kind;
        if (kind === 'note' || inside.has(k) || isOverlayKind(kind))
            continue;
        return canCarry(kind) ? k : null;
    }
    return null;
}
/**
 * The `storylineAt` map after the boxes changed: a moved box takes its second along (its key renumbers), an edited box
 * keeps it, a removed box drops it. Boxes are matched by identity (the panel keeps the objects it does not touch), an
 * edited box by its position.
 */
export function rekeyStorylineAt(from, to, map) {
    const before = boxKeys(from);
    const after = boxKeys(to);
    const out = {};
    const matched = new Set();
    to.forEach((b, k) => {
        let i = from.indexOf(b);
        if (i < 0 && from.length === to.length && from[k].kind === b.kind)
            i = k;
        if (i < 0 || matched.has(i))
            return;
        matched.add(i);
        const was = before[i];
        const now = after[k];
        if (was && now && map[was] !== undefined)
            out[now] = map[was];
    });
    return out;
}
export function storylineAnchors(blocks) {
    const counts = new Map();
    return blocks.map((b) => {
        if (b.kind !== 'video' && b.kind !== 'title' && b.kind !== 'sot' && b.kind !== 'reporter' && b.kind !== 'card')
            return null;
        const label = normalizeMarker(storylineHeading(b));
        const n = (counts.get(label) ?? 0) + 1;
        counts.set(label, n);
        return `${label}#${n}`;
    });
}
/** The CG template a box's title would use, and its lines. */
export function storylineCg(b) {
    switch (b.kind) {
        case 'title': {
            const lines = b.lines.map((l) => l.trim()).filter(Boolean);
            return { template: lines.length >= 2 ? 'lower-third-2' : 'lower-third-1', lines };
        }
        case 'sot':
            return { template: 'lower-third-2', lines: [b.name.trim(), b.role.trim()].filter(Boolean) };
        case 'reporter':
            return { template: 'credit', lines: [b.name.trim(), b.camera.trim()].filter(Boolean) };
        case 'card':
            return { template: 'fullscreen', lines: b.lines.map((l) => l.trim()).filter(Boolean) };
        default:
            return null;
    }
}
/** A new, empty box of a kind; titles and cards take the next free number. */
export function newStorylineBlock(kind, blocks) {
    const next = (k) => blocks.reduce((m, b) => (b.kind === k && b.n !== null ? Math.max(m, b.n) : m), 0) + 1;
    switch (kind) {
        case 'read':
            return { kind: 'read', mode: 'on', text: '' };
        case 'video':
            return { kind: 'video', sound: true, note: '' };
        case 'title':
            return { kind: 'title', n: next('title'), lines: [''] };
        case 'sot':
            return { kind: 'sot', name: '', role: '', text: '' };
        case 'reporter':
            return { kind: 'reporter', name: '', email: '', camera: '' };
        case 'card':
            return { kind: 'card', n: next('card'), lines: [''] };
        case 'natsound':
            return { kind: 'natsound' };
        case 'promo':
            return { kind: 'promo', lines: [''] };
        case 'note':
            return { kind: 'note', text: '' };
    }
}
//# sourceMappingURL=storyline.js.map